// =============================================================================
// Optim TURPE V2 — LA COMPARAISON AVEC LA DERNIÈRE ÉTUDE DE L'ANCIENNE VERSION
// (décision D29 du 05/10/2026, maquette « 1 · Le pont avec l'ancienne
// version », amendée par le mot du vote : « c'est un encadré dans l'étude en
// comparaison à la dernière étude, c'est pas au premier plan »).
//
// Ce que le client doit vivre : sur un compteur déjà étudié dans l'ancienne
// version, il retrouve DANS l'étude, replié sous le montant, sa dernière étude
// (date, montant), ce que la nouvelle version trouve à la place et les seules
// raisons de l'écart qui jouent sur CE compteur, puis un lien vers l'étude
// d'origine. Sans étude de l'ancienne version, rien ne s'affiche.
//
// Règles tenues ici :
// - aucun montant n'est calculé : l'ancienne étude donne son gain
//   (`economies_positives_avec_cta`, celui que sa page affiche), la nouvelle
//   son montant de la période analysée ; chacun se dit avec son nombre de
//   jours (« sur 365 jours »), l'unité de toute la page (finitions du
//   07/10/2026), jamais « par an » ; une absence n'est jamais un 0 ;
// - chaque raison se décide sur des champs servis par les deux études ; une
//   raison que la donnée ne permet pas de trancher ne s'affiche pas ;
// - la contribution tarifaire n'est plus une raison depuis le 05/10 (D38) :
//   les deux versions appliquent le même taux.
// =============================================================================

import { formateurFr } from "@modules/courtage/utils/optim-turpe-v2.nombre"
import {
    ETATS_DE_GAIN,
    lireChiffre,
    lirePeriode,
} from "@modules/courtage/utils/optim-turpe-v2.chiffre"
import { lireArbitrageServi } from "@modules/courtage/utils/optim-turpe-v2.arbitrage"
import { surLaPeriode } from "@modules/courtage/utils/optim-turpe-v2.titre"
import { formatDateHeureParis } from "@modules/courtage/utils/optim-turpe-v2.helpers"
import {
    formatMontantSigne,
    nombreEuros,
    sensMontant,
} from "@modules/courtage/utils/optim-turpe-v2.montant"
import type {
    OptimTurpeV2Etude,
    OptimTurpeV2PlageKey,
} from "@modules/courtage/types/optim-turpe-v2"
import type { OptimTurpeAnalyse } from "@modules/courtage/types/optim-turpe"

/** Les classes de la saison haute : leur pointe est une pointe d'hiver. */
const CLASSES_D_HIVER = new Set(["HPH", "HCH", "P", "PTE"])

/** Le nom des classes horaires, en langage de client. */
const NOM_DES_CLASSES: Record<OptimTurpeV2PlageKey, string> = {
    // Vague wording du 14/10/2026 (Q6, W61) : le poste avec son code, la
    // saison haute et la saison basse, jamais « hiver » ni « été ».
    PTE: "heures de pointe (PTE)",
    HPH: "heures pleines de saison haute (HPH)",
    HCH: "heures creuses de saison haute (HCH)",
    HPB: "heures pleines de saison basse (HPB)",
    HCB: "heures creuses de saison basse (HCB)",
}
const ORDRE_DES_CLASSES: OptimTurpeV2PlageKey[] = [
    "PTE",
    "HPH",
    "HCH",
    "HPB",
    "HCB",
]

const nombreFr = formateurFr({ maximumFractionDigits: 0 })

function fini(valeur: unknown): number | null {
    return typeof valeur === "number" && Number.isFinite(valeur) ? valeur : null
}

/** Une valeur servie de l'ancienne étude (`data.<cle>.value`). */
function valeurV1(v1: OptimTurpeAnalyse, cle: string): unknown {
    return v1.data?.[cle]?.value
}

/** « 01/10/2026 » → nombre de jours depuis l'époque, ou `null`. */
function jourFr(fr: string): number | null {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(fr.trim())
    if (!m) return null
    return Math.round(Date.UTC(+m[3], +m[2] - 1, +m[1]) / 86_400_000)
}

/** « 2025-10-01 » → « 01/10/2025 », ou `null`. */
function dateFr(iso: string | null): string | null {
    const m = iso ? /^(\d{4})-(\d{2})-(\d{2})/.exec(iso) : null
    return m ? `${m[3]}/${m[2]}/${m[1]}` : null
}

/**
 * Un montant signé à la convention de la page (revue du 08/10, R1) :
 * « +433 € HT », « −362 € HT » ; 0 reste « 0 € HT ».
 */
function euros(eur: number): string {
    return formatMontantSigne(eur, "HT") ?? ""
}

/** « 58 € d'économie », « 36 € de surcoût », « 0 € » : la forme nominale (D1 b). */
function nominal(eur: number): string {
    const sens = sensMontant(eur)
    if (sens === "nul") return "0\u00a0€"
    return `${nombreEuros(eur)}\u00a0€ ${sens === "gain" ? "d'économie" : "de surcoût"}`
}

/** « JJ/MM/AAAA » → nombre de jours depuis l'époque, ou `null`. */
function jourDe(date: string): number | null {
    const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(date)
    if (!m) return null
    return Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])) / 86_400_000
}

/** Au-delà de sept jours d'écart, deux périodes expliquent un écart (R22, C37). */
const ECART_DE_PERIODE_JOURS = 7

// -----------------------------------------------------------------------------
// Ce qu'on lit des deux études
// -----------------------------------------------------------------------------

/** La période de l'ancienne étude, servie sous la forme « JJ/MM/AAAA - JJ/MM/AAAA ». */
export type PeriodeV1 = {
    du: string
    au: string
    /** Jours calendaires entre les deux bornes, ou `null`. */
    jours: number | null
}

export function lirePeriodeV1(v1: OptimTurpeAnalyse): PeriodeV1 | null {
    const brute = valeurV1(v1, "period")
    if (typeof brute !== "string") return null
    const m = /^\s*(\d{2}\/\d{2}\/\d{4})\s*-\s*(\d{2}\/\d{2}\/\d{4})\s*$/.exec(
        brute
    )
    if (!m) return null
    const debut = jourFr(m[1])
    const fin = jourFr(m[2])
    return {
        du: m[1],
        au: m[2],
        jours: debut !== null && fin !== null ? fin - debut : null,
    }
}

/** Les puissances d'une structure de l'ancienne étude (`P_<classe>_avant|apres`). */
function puissancesV1(
    v1: OptimTurpeAnalyse,
    quand: "avant" | "apres"
): Partial<Record<OptimTurpeV2PlageKey, number>> {
    const lues: Partial<Record<OptimTurpeV2PlageKey, number>> = {}
    for (const classe of ORDRE_DES_CLASSES) {
        const v = fini(valeurV1(v1, `P_${classe}_${quand}`))
        if (v !== null) lues[classe] = v
    }
    return lues
}

/** L'ancienne étude garde la structure actuelle et rend 0 €. */
function gardeLeContratSansGain(v1: OptimTurpeAnalyse): boolean {
    const gain = fini(valeurV1(v1, "economies_positives_avec_cta"))
    if (gain === null || Math.round(gain) !== 0) return false
    const avant = puissancesV1(v1, "avant")
    const apres = puissancesV1(v1, "apres")
    const classes = Object.keys(apres) as OptimTurpeV2PlageKey[]
    return (
        classes.length > 0 &&
        classes.every((c) => avant[c] === undefined || avant[c] === apres[c])
    )
}

/**
 * L'ancienne étude acceptait des dépassements ET proposait un changement :
 * elle sert des dépassements après changement (`cmdps_apres` positif, vérifié
 * par le seul appelant, `raisonDepassements`), un gain positif, et une
 * structure conseillée différente de l'actuelle (une puissance ou la formule).
 * Sans ces trois preuves, rien ne lui est attribué.
 */
function ancienneAcceptaitLesDepassements(v1: OptimTurpeAnalyse): boolean {
    const gain = fini(valeurV1(v1, "economies_positives_avec_cta"))
    if (gain === null || Math.round(gain) <= 0) return false
    const avant = puissancesV1(v1, "avant")
    const apres = puissancesV1(v1, "apres")
    const puissance = (Object.keys(apres) as OptimTurpeV2PlageKey[]).some(
        (c) => avant[c] !== undefined && avant[c] !== apres[c]
    )
    const ftaAvant = valeurV1(v1, "fta_avant")
    const ftaApres = valeurV1(v1, "fta_apres")
    const formule =
        typeof ftaAvant === "string" &&
        typeof ftaApres === "string" &&
        ftaAvant !== ftaApres
    return puissance || formule
}

/** Les puissances que propose l'étude affichée, par classe. */
function puissancesProposees(
    etude: OptimTurpeV2Etude
): Partial<Record<OptimTurpeV2PlageKey, number>> {
    const servies =
        etude.meilleure?.structure_tarifaire?.puissance_souscrite_soutirage
            ?.denivele ?? {}
    const lues: Partial<Record<OptimTurpeV2PlageKey, number>> = {}
    for (const classe of ORDRE_DES_CLASSES) {
        const v = fini(servies[classe])
        if (v !== null) lues[classe] = v
    }
    return lues
}

/** L'étude sert un état de gain ET un montant positif. */
function estUnGainServi(etude: OptimTurpeV2Etude): boolean {
    const etat = lireChiffre(etude).etat
    if (etat === null || !ETATS_DE_GAIN.includes(etat)) return false
    const montant = lireMontantV2(etude)
    return montant !== null && montant.eur > 0
}

/** Un montant de la nouvelle version : celui de la période analysée. */
export type MontantV2 = {
    eur: number
    /** Le nombre de jours de la période analysée, ou `null`. */
    jours: number | null
    /** Ses dates, pour « sur la période du … au … » (Q15), si elles sont lues. */
    dates?: { dateFrom: string | null; dateTo: string | null }
}

/** Le montant de la période analysée, sinon `null`. */
export function lireMontantV2(
    etude: OptimTurpeV2Etude | null | undefined
): MontantV2 | null {
    if (!etude) return null
    const chiffre = lireChiffre(etude)
    if (chiffre.netteEur === null) return null
    const { dateFrom, dateTo } = chiffre.periode
    return dateFrom || dateTo
        ? {
              eur: chiffre.netteEur,
              jours: chiffre.periode.jours,
              dates: { dateFrom, dateTo },
          }
        : { eur: chiffre.netteEur, jours: chiffre.periode.jours }
}

/**
 * « +213 € HT par an » sur 365 ou 366 jours, sinon « … sur la période du … au
 * … », ou « … sur la période étudiée » sans dates (Q15).
 */
export function libelleMontantV2(montant: MontantV2): string {
    return `${euros(montant.eur)} ${surLaPeriode(montant.jours, montant.dates)}`
}

// -----------------------------------------------------------------------------
// Les raisons de l'écart
// -----------------------------------------------------------------------------

export type CodeRaison =
    | "depassements"
    | "plancher_250_kw"
    | "plancher_37_kva"
    | "raccordement"
    | "periode"

export type RaisonDeLEcart = {
    code: CodeRaison
    titre: string
    phrase: string
    /** Le bouton « Comparer les deux scénarios » suit la raison. */
    comparer: boolean
}

export type ComparaisonV1 = {
    /** Date de l'ancienne étude, « 01/10/2026 » (heure de Paris). */
    dateEtude: string
    /** Gain de l'ancienne étude, arrondi, ou `null` s'il n'est pas servi. */
    montantV1Eur: number | null
    /** Le nombre de jours de la période de l'ancienne étude, ou `null`. */
    montantV1Jours: number | null
    /** Le montant de l'étude affichée, ou `null` s'il n'est pas servi. */
    ici: MontantV2 | null
    /**
     * Les deux scénarios, quand la raison « dépassements » joue.
     * `ancienneLesAcceptait` : les données de l'ancienne étude prouvent
     * qu'elle acceptait des dépassements en proposant un changement ; sinon
     * aucun texte ne le lui attribue.
     */
    scenarios: {
        sans: MontantV2
        avec: MontantV2
        hiver: boolean
        ancienneLesAcceptait: boolean
    } | null
    raisons: RaisonDeLEcart[]
    /**
     * Les deux périodes analysées, quand elles diffèrent et sont servies
     * toutes les deux (la raison « période » existe alors) ; `null` sinon.
     * L'encadré les écrit sans relire la phrase de la raison.
     */
    periodes: {
        ancienne: { du: string; au: string }
        celleCi: { du: string; au: string }
    } | null
}

type Entrees = {
    v1: OptimTurpeAnalyse | null | undefined
    etude: OptimTurpeV2Etude | null | undefined
    sans?: OptimTurpeV2Etude | null
    avec?: OptimTurpeV2Etude | null
}

/**
 * Règle de l'ancienne version énoncée quand ses données le prouvent (dépôt
 * data, 08ef03c39, modules/analyses/) : le prix de chaque proposition compte
 * ses dépassements (optimturpe_C2/mixins/compute_turpe_optimisable_C2.py:58 et
 * optimturpe_C4/mixins/compute_turpe_optimisable_C4.py:51, total = part fixe
 * + part variable + dépassements ; appelés par optimturpe_C2/tasks/
 * analyse_optimturpe_C2.py:447-462 et optimturpe_C4/tasks/
 * analyse_optimturpe_C4.py:529-542) et la formule retenue est celle qui gagne
 * le plus, donc la moins chère (C2 :481, C4 :546). Le fait propre au compteur
 * (« la structure qu'elle retenait ici en payait ») est `cmdps_apres` servi et
 * positif.
 *
 * Dépassements : l'ancienne étude retenait une structure qui paie des
 * dépassements (`cmdps_apres` servi et positif), et la nouvelle conseille de
 * couvrir les dépassements alors que son scénario avec dépassements est un gain plus
 * élevé. Sans `cmdps_apres`, on ne sait pas ce que retenait l'ancienne étude :
 * la raison ne s'affiche pas.
 */
function raisonDepassements(
    v1: OptimTurpeAnalyse,
    sans: OptimTurpeV2Etude | null | undefined,
    avec: OptimTurpeV2Etude | null | undefined,
    borneProuvee: boolean
): {
    raison: RaisonDeLEcart
    scenarios: NonNullable<ComparaisonV1["scenarios"]>
} | null {
    const cmdpsV1 = fini(valeurV1(v1, "cmdps_apres"))
    if (cmdpsV1 === null || cmdpsV1 <= 0) return null
    // L'ancienne étude qui garde le contrat actuel sans rien trouver ne
    // « retient » pas ses dépassements : ce sont ceux du contrat. Quand une
    // borne de l'ancienne version (plancher, raccordement) est prouvée sur ce
    // compteur, c'est elle qui l'empêchait de bouger, pas un arbitrage.
    if (borneProuvee && gardeLeContratSansGain(v1)) return null
    const arbitrage = lireArbitrageServi(sans ?? null, avec ?? null)
    if (!arbitrage || arbitrage.conseil !== "sans" || !sans || !avec) {
        return null
    }
    if (!estUnGainServi(avec)) return null
    const montantSans = lireMontantV2(sans)
    const montantAvec = lireMontantV2(avec)
    if (!montantSans || !montantAvec) return null
    if (montantSans.jours !== montantAvec.jours) return null
    if (montantAvec.eur <= montantSans.eur) return null

    const hiver =
        arbitrage.plage !== null && CLASSES_D_HIVER.has(arbitrage.plage)
    // La durée servie (`duree_depassement_h`) est celle du scénario AVEC
    // dépassements (courtage origin/main 4cab161, modules/optimturpe/
    // arbitrage.py:288 `_duree_de_depassement(avec)`, servie :323, phrase
    // « L'assumer exposerait à environ X h » :292), somme de toutes les
    // classes horaires (:151) : elle se dit donc avec « si vous acceptez ces
    // dépassements », jamais avec la structure conseillée, et sans nom de
    // classe. Les heures se comptent sur la période analysée, dite en jours.
    const heures = arbitrage.dureeDepassementH
    const precision =
        heures !== null && heures >= 1 && montantSans.jours !== null
            ? ` (environ ${nombreFr.format(heures)} ${
                  Math.round(heures) > 1 ? "heures" : "heure"
              } ${surLaPeriode(montantSans.jours, montantSans.dates)})`
            : ""
    const ancienneLesAcceptait = ancienneAcceptaitLesDepassements(v1)
    const options = `Cette étude chiffre les deux scénarios et recommande ici de couvrir les dépassements${hiver ? " de saison haute" : ""}. `
    return {
        scenarios: {
            sans: montantSans,
            avec: montantAvec,
            hiver,
            ancienneLesAcceptait,
        },
        raison: {
            code: "depassements",
            titre: "Les dépassements de puissance",
            phrase:
                (ancienneLesAcceptait
                    ? "L'ancienne version comptait le coût des dépassements dans le prix de chaque proposition et retenait la moins chère. " +
                      "Les puissances qu'elle retenait ici en payaient. "
                    : "") +
                options +
                `En assumant ces dépassements${precision}, l'économie estimée est de ${libelleMontantV2(montantAvec)}.`,
            comparer: true,
        },
    }
}

/**
 * Les règles de l'ancienne version que l'encadré énonce, lues dans son code
 * (dépôt data, branche principale 08ef03c39, déployée en production le
 * 01/10/2026), avec la date du commit qui les a introduites : une étude
 * calculée avant cette date n'en relevait pas, la raison s'y tait.
 *
 * - Plancher de 250 kW en haute tension :
 *   modules/analyses/optimturpe_C2/tasks/analyse_optimturpe_C2.py:82,133-134
 *   (puissance_min = 250 par défaut), passé à l'optimisation :392,400 ;
 *   mixins/optimize_turpe_C2.py:114 (max(puissance, puissance_min)) ; présent
 *   en dur depuis le 24/01/2024 (18ddc70a3).
 * - Contrat conservé quand aucune proposition ne gagne :
 *   analyse_optimturpe_C2.py:481 (la formule au plus fort gain), :488-491
 *   (gain <= 0 : retour au contrat actuel), :531-532 (écart servi, gain
 *   affiché max(0, écart)).
 * - Plancher de 37 kVA sur chaque classe en basse tension au-dessus de 36 kVA :
 *   modules/analyses/optimturpe_C4/tasks/analyse_optimturpe_C4.py:87,137-138
 *   (puissance_min = 37), :475-479 (max(37, puissance) sur chaque classe) ;
 *   depuis le 09/02/2026 (bf303b7d5).
 */
const PLANCHER_250_DEPUIS = "2024-01-24"
const PLANCHER_37_DEPUIS = "2026-02-09"

/** L'ancienne étude a-t-elle été calculée à partir de `depuis` (AAAA-MM-JJ) ? */
function calculeeDepuis(v1: OptimTurpeAnalyse, depuis: string): boolean {
    const quand = v1.createdAt ?? v1.updatedAt
    return typeof quand === "string" && quand.slice(0, 10) >= depuis
}

/** « 164 kW », ou « de 120 à 164 kW » quand les puissances servies diffèrent. */
function etenduePuissances(puissances: number[], unite: string): string {
    const basse = Math.min(...puissances)
    const haute = Math.max(...puissances)
    return basse === haute
        ? `${nombreFr.format(haute)} ${unite}`
        : `de ${nombreFr.format(basse)} à ${nombreFr.format(haute)} ${unite}`
}

/**
 * Plancher de 250 kW. L'encadré n'énonce que la règle de l'ancienne version
 * (lue dans son code, ci-dessus) et des valeurs servies : le compteur est en
 * haute tension (segment C2 servi), souscrit sous 250 kW (puissances
 * actuelles servies par l'ancienne étude), l'ancienne étude sert un écart
 * négatif (`economies_avec_cta`, donc aucune formule ne gagnait et le contrat
 * était conservé) et un gain de 0 ; l'étude affichée sert un gain et propose
 * au moins une puissance sous 250 kW. Sinon, rien.
 */
function raisonPlancher250(
    v1: OptimTurpeAnalyse,
    etude: OptimTurpeV2Etude,
    montantV1Eur: number | null
): RaisonDeLEcart | null {
    if (etude.compteur?.segment !== "C2") return null
    if (!calculeeDepuis(v1, PLANCHER_250_DEPUIS)) return null
    if (montantV1Eur !== 0) return null
    const brut = fini(valeurV1(v1, "economies_avec_cta"))
    if (brut === null || brut >= 0) return null
    const souscrites = Object.values(puissancesV1(v1, "avant"))
    if (souscrites.length === 0) return null
    const haute = Math.max(...souscrites)
    if (haute >= 250) return null
    if (!estUnGainServi(etude)) return null
    const proposees = Object.values(puissancesProposees(etude)).filter(
        (p): p is number => p !== undefined
    )
    if (!proposees.some((p) => p < 250)) return null
    const souscrit =
        Math.min(...souscrites) === haute
            ? `à ${nombreFr.format(haute)} kW`
            : `à ${nombreFr.format(haute)} kW au plus`
    return {
        code: "plancher_250_kw",
        titre: "Le plancher de 250 kW",
        phrase:
            "L'ancienne version ne proposait jamais une puissance inférieure à 250 kW sur un compteur haute tension, et conservait le contrat actuel quand aucune proposition n'était moins chère. " +
            `Ce compteur est souscrit ${souscrit}. L'ancienne étude a conservé ce contrat et conclu à l'absence d'économie. ` +
            `Celle-ci propose ${etenduePuissances(proposees, "kW")}.`,
        comparer: false,
    }
}

/**
 * Plancher de 37 kVA : compteur basse tension au-dessus de 36 kVA (segment C4
 * servi), ancienne étude calculée sous la règle, qui recommande exactement
 * 37 kVA sur une classe que l'étude affichée place plus bas.
 */
function raisonPlancher37(
    v1: OptimTurpeAnalyse,
    etude: OptimTurpeV2Etude
): RaisonDeLEcart | null {
    if (etude.compteur?.segment !== "C4") return null
    if (!calculeeDepuis(v1, PLANCHER_37_DEPUIS)) return null
    const recommandeesV1 = puissancesV1(v1, "apres")
    const proposees = puissancesProposees(etude)
    const classes = ORDRE_DES_CLASSES.filter((classe) => {
        const ancienne = recommandeesV1[classe]
        const nouvelle = proposees[classe]
        return ancienne === 37 && nouvelle !== undefined && nouvelle < 37
    })
    if (classes.length === 0) return null
    const detail = classes
        .map(
            (c) =>
                `${nombreFr.format(proposees[c] as number)} kVA en ${NOM_DES_CLASSES[c]}`
        )
        .join(", ")
    return {
        code: "plancher_37_kva",
        titre: "Le plancher de 37 kVA",
        phrase:
            "L'ancienne version ne proposait jamais moins de 37 kVA, sur chaque poste. " +
            `Celle-ci propose ${detail}, au lieu de 37 kVA.`,
        comparer: false,
    }
}

/**
 * Raccordement (basse tension) : l'ancienne version bornait sa recherche à la
 * puissance de raccordement (optimturpe_C4/tasks/analyse_optimturpe_C4.py:473,
 * min(250, raccordement)) ; l'ancienne étude sert ce raccordement et sa
 * recommandation la plus haute l'égale, et l'étude affichée propose davantage
 * sur au moins une classe. Aucun conseil n'est ajouté : seules les deux
 * valeurs servies sont dites.
 */
function raisonRaccordement(
    v1: OptimTurpeAnalyse,
    etude: OptimTurpeV2Etude
): RaisonDeLEcart | null {
    if (etude.compteur?.segment !== "C4") return null
    const raccordement = fini(valeurV1(v1, "puissance_raccordement"))
    if (raccordement === null || raccordement <= 0) return null
    const recommandees = Object.values(puissancesV1(v1, "apres"))
    if (recommandees.length === 0) return null
    // « ne proposait pas plus que » : sa recommandation la plus haute est
    // exactement la puissance de raccordement lue, ni moins, ni plus.
    if (Math.max(...recommandees) !== raccordement) return null
    const proposees = Object.values(puissancesProposees(etude)).filter(
        (p): p is number => p !== undefined
    )
    if (proposees.length === 0) return null
    const plusHaute = Math.max(...proposees)
    if (plusHaute <= raccordement) return null
    return {
        code: "raccordement",
        titre: "La puissance de raccordement",
        phrase:
            `L'ancienne étude ne proposait pas plus que la puissance de raccordement qu'elle lisait, ${nombreFr.format(raccordement)} kVA. ` +
            `Celle-ci propose jusqu'à ${nombreFr.format(plusHaute)} kVA.`,
        comparer: false,
    }
}

/**
 * Période : les deux périodes analysées, telles que les deux études les
 * servent, quand elles diffèrent. Rien sur l'effet de l'écart, qu'aucune
 * donnée ne chiffre ; si l'une des deux périodes n'est pas servie, rien.
 */
function periodesQuiDifferent(
    v1: OptimTurpeAnalyse,
    etude: OptimTurpeV2Etude
): ComparaisonV1["periodes"] {
    const ancienne = lirePeriodeV1(v1)
    const nouvelle = lirePeriode(etude)
    const du = dateFr(nouvelle.dateFrom)
    const au = dateFr(nouvelle.dateTo)
    if (!ancienne || !du || !au) return null
    if (ancienne.du === du && ancienne.au === au) return null
    // C37 : un jour d'écart n'explique rien ; la ligne et la raison se taisent
    // sous sept jours d'écart sur le début et sur la fin.
    const ecart = (a: string, b: string) => {
        const x = jourDe(a)
        const y = jourDe(b)
        return x === null || y === null ? Infinity : Math.abs(x - y)
    }
    if (
        ecart(ancienne.du, du) <= ECART_DE_PERIODE_JOURS &&
        ecart(ancienne.au, au) <= ECART_DE_PERIODE_JOURS
    )
        return null
    return {
        ancienne: { du: ancienne.du, au: ancienne.au },
        celleCi: { du, au },
    }
}

function raisonPeriode(
    v1: OptimTurpeAnalyse,
    etude: OptimTurpeV2Etude
): RaisonDeLEcart | null {
    const periodes = periodesQuiDifferent(v1, etude)
    if (!periodes) return null
    const ancienne = periodes.ancienne
    const { du, au } = periodes.celleCi
    return {
        code: "periode",
        titre: "La période analysée",
        phrase: `L'ancienne étude portait sur la période du ${ancienne.du} au ${ancienne.au}, celle-ci sur la période du ${du} au ${au}.`,
        comparer: false,
    }
}

type AncienneVersion = Pick<
    ComparaisonV1,
    "dateEtude" | "montantV1Eur" | "montantV1Jours"
>

/** Date et gain de l'ancienne étude aboutie et datée, ou `null`. */
function lireAncienneVersion(
    v1: OptimTurpeAnalyse | null | undefined
): AncienneVersion | null {
    if (!v1 || v1.status !== "SUCCESS") return null
    const date = formatDateHeureParis(v1.createdAt ?? v1.updatedAt)
    if (date === "—") return null
    const gain = fini(valeurV1(v1, "economies_positives_avec_cta"))
    const periodeV1 = lirePeriodeV1(v1)
    return {
        dateEtude: date.split(" à ")[0],
        montantV1Eur: gain === null ? null : Math.round(gain),
        montantV1Jours: periodeV1?.jours ?? null,
    }
}

/**
 * Pendant le calcul (maquette mq-attente, D29) : la ligne de la dernière
 * étude de l'ancienne version et ce qui en sera fait, ou `null` sans étude
 * ancienne aboutie. L'encadré de comparaison s'ouvre avec l'étude, dès
 * qu'elle aboutit.
 */
export function lireAttenteAncienneVersion(
    v1: OptimTurpeAnalyse | null | undefined
): { ligne: string; suite: string } | null {
    const ancienne = lireAncienneVersion(v1)
    if (!ancienne) return null
    const c = {
        ...ancienne,
        ici: null,
        scenarios: null,
        raisons: [],
        periodes: null,
    }
    return {
        ligne: ligneAncienneVersion(c),
        suite:
            ancienne.montantV1Eur === null
                ? "Le nouveau calcul sera comparé à cette étude dès qu'il aboutit."
                : "Le nouveau calcul sera comparé à ce montant dès qu'il aboutit.",
    }
}

/**
 * La comparaison de l'étude affichée avec la dernière étude de l'ancienne
 * version, ou `null` quand il n'y en a pas (aucun encadré).
 */
export function lireComparaisonV1({
    v1,
    etude,
    sans,
    avec,
}: Entrees): ComparaisonV1 | null {
    if (!v1 || !etude) return null
    const ancienne = lireAncienneVersion(v1)
    if (!ancienne) return null
    const { dateEtude, montantV1Eur, montantV1Jours } = ancienne

    const plancher250 = raisonPlancher250(v1, etude, montantV1Eur)
    const plancher37 = raisonPlancher37(v1, etude)
    const raccordement = raisonRaccordement(v1, etude)
    const depassements = raisonDepassements(
        v1,
        sans,
        avec,
        plancher250 !== null || plancher37 !== null || raccordement !== null
    )
    const raisons = [
        depassements?.raison ?? null,
        plancher250,
        plancher37,
        raccordement,
        raisonPeriode(v1, etude),
    ].filter((r): r is RaisonDeLEcart => r !== null)

    return {
        dateEtude,
        montantV1Eur,
        montantV1Jours,
        ici: lireMontantV2(etude),
        scenarios: depassements?.scenarios ?? null,
        raisons,
        periodes: periodesQuiDifferent(v1, etude),
    }
}

// -----------------------------------------------------------------------------
// Les textes de l'encadré
// -----------------------------------------------------------------------------

/**
 * « +433 € HT sur 365 jours », « aucune économie estimée », ou `null`.
 * DEV-2432 : une absence de montant ne s'écrit jamais « 0 € » (règle du
 * 07/09/2026) ; la page de production disait « (0 €) » le 19/10/2026.
 */
export function libelleMontantV1(c: ComparaisonV1): string | null {
    if (c.montantV1Eur === null) return null
    if (c.montantV1Eur === 0) return "aucune économie estimée"
    const unite =
        c.montantV1Jours !== null && c.montantV1Jours > 0
            ? surLaPeriode(c.montantV1Jours)
            : "sur sa période"
    return `${euros(c.montantV1Eur)} ${unite}`
}

/** « Ancienne version · +433 € HT sur 365 jours, étude du 01/10/2026 ». */
export function ligneAncienneVersion(c: ComparaisonV1): string {
    const montant = libelleMontantV1(c)
    return montant
        ? `Ancienne version · ${montant}, étude du ${c.dateEtude}`
        : `Ancienne version · étude du ${c.dateEtude}`
}

/**
 * Revue 2 en production (I16, I32, I38) : la ligne des deux montants de
 * l'encadré, « Ancienne version : +58 € HT par an (étude du 14/10/2026) ·
 * celle-ci : +72 € HT par an. » Avec la paire des scénarios, celle-ci dit ses
 * deux montants ; « dépassements assumés » n'est dit de l'ancienne étude que
 * quand sa donnée le prouve.
 */
export function ligneDesMontants(c: ComparaisonV1): string {
    // D1 (b) : dans une phrase, la forme nominale, sans signe.
    const unite = (jours: number | null) =>
        jours !== null && jours > 0 ? surLaPeriode(jours) : "sur sa période"
    const ancienne =
        c.montantV1Eur === null
            ? null
            : c.montantV1Eur === 0
              ? "aucune économie estimée"
              : `${nominal(c.montantV1Eur)} ${unite(c.montantV1Jours)}`
    const acceptait = c.scenarios?.ancienneLesAcceptait
        ? ", dépassements assumés"
        : ""
    // « aucune économie estimée » n'a pas d'unité : la date suit après une
    // virgule, sans parenthèses.
    const gauche =
        c.montantV1Eur === 0
            ? `Ancienne version : ${ancienne}, étude du ${c.dateEtude}${acceptait}`
            : ancienne
              ? `Ancienne version : ${ancienne} (étude du ${c.dateEtude}${acceptait})`
              : `Ancienne version : étude du ${c.dateEtude}${acceptait}`
    const ici = (m: MontantV2) =>
        `${nominal(m.eur)} ${surLaPeriode(m.jours, m.dates)}`
    const droite = c.scenarios
        ? `${ici(c.scenarios.sans)} en couvrant les dépassements, ${ici(c.scenarios.avec)} en les assumant`
        : c.ici
          ? ici(c.ici)
          : "économie non chiffrée"
    return `${gauche} · celle-ci : ${droite}.`
}

/**
 * « Périodes : 13/10/2025 → 13/10/2026 (ancienne), 14/10/2025 → 13/10/2026
 * (celle-ci). », lue sur les périodes servies ; `null` quand elles sont
 * identiques ou non servies (relecture sceptique de la revue 2 : plus de
 * relecture de la phrase de la raison).
 */
export function lignePeriodes(c: ComparaisonV1): string | null {
    const p = c.periodes
    if (!p) return null
    return `Périodes : ${p.ancienne.du} → ${p.ancienne.au} (ancienne), ${p.celleCi.du} → ${p.celleCi.au} (celle-ci).`
}

/** Le noyau de l'en-tête replié : l'essentiel sans rien déplier. */
export function noyauDeLEntete(c: ComparaisonV1): string {
    // Maquette allégée : « +3 127 € HT sur 366 jours le 21/09/2026 », la pastille
    // « Ancien » dit la version.
    // Contrat v1.2 §6 6.1 : le résumé nomme la version (« Ancienne
    // version : +58 € HT par an le 14/10/2026 »), montant seul signé.
    const montant = libelleMontantV1(c)
    return montant
        ? `Ancienne version : ${montant} le ${c.dateEtude}`
        : `Ancienne version : étude du ${c.dateEtude}`
}

/**
 * D8 (a) : la comparaison s'affiche au client seulement quand une raison
 * hors période est prouvée par les champs servis ; toujours à l'équipe.
 */
export function comparaisonVisible(
    c: ComparaisonV1 | null,
    equipe: boolean
): boolean {
    if (!c) return false
    if (equipe) return true
    return c.raisons.some((r) => r.code !== "periode")
}
